import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import AnimatedCard from "./AnimatedCard";
import UnlockSmart1 from "../assets/images/unlockSmarter1.png";
import UnlockSmart2 from "../assets/images/unlockSmarter2.png";
import UnlockSmart3 from "../assets/images/unlockSmart3.png";
import UnlockSmart4 from "../assets/images/unlockSmart4.png";

export default function SmartCard() {
  const cards = [
    {
      id: 1,
      title: "Learn Anytime, Even During the Holidays",
      description:
        "Keep the learning momentum going! Bildup AI ensures your child can continue to practice and reinforce their skills across different subjects during school breaks, preventing learning loss.",
      LeftTitle: "Master Your Curriculum With AI-Powered Personalized Learning",
      LeftDescription:
        "Struggling to keep up with your classwork? Bildup's AI-driven learning system adapts to your unique needs, breaking down complex topics into easy-to-understand lessons tailored just for you. Learn at your own pace and stay ahead in every subject!",
      iconColor: "text-amber-500",
      bgColor: "bg-amber-100",
      direction: "right",
      video: UnlockSmart1,
      LeftVideo: UnlockSmart2,
    },
    {
      id: 2,
      LeftTitle: "Homework Help Without the Headaches",
      LeftDescription:
        "No more stressful evenings battling with assignments! Our AI tutor walks your child through their homework across different subjects, providing guidance and explanations to ensure they truly understand the material.",
      title: "Conquer Difficult Concepts in Any Subject",
      description:
        "Experience the power of our 24/7 Standby Intelligent Tutor. Whether it's solving homework challenges at midnight or preparing for exams early in the morning, the Bildup AI tutor ensures that learning never takes a backseat.",
      icon: "ph:chart-line-up-duotone",
      iconColor: "text-amber-500",
      bgColor: "bg-amber-100",
      direction: "right",
      LeftVideo: UnlockSmart3,
      video: UnlockSmart4,
    },
  ];

  return (
    <section className="py-16 px-4">
      <div className="max-w-screen-xl mx-auto">
        <div className="text-center mb-16">
          <AnimatedCard
            direction="fadeIn"
            duration={0.8}
            once={true}
            className="flex flex-col items-center justify-center gap-4"
          >
            <p className="text-primary-purple-400">
              AI Powered Personalized Learning Experience With Bildup
            </p>
            <h2 className="text-2xl md:text-[48px] flex flex-col gap-4 mb-4">
              Unlock a Smarter Way to Learn <br />
              <span>With Bildup AI</span>
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              From mastering concepts and acing tests to building future-ready
              skills, Bildup AI makes learning smarter, faster, and fun.
            </p>
          </AnimatedCard>
        </div>

        {/* Carousel on mobile */}
        <div className="block md:hidden">
          <Swiper
            modules={[Pagination]}
            pagination={{ clickable: true }}
            spaceBetween={30}
            slidesPerView={1}
            className="!pb-8" // add space for dots at bottom
          >
            {cards.map((card) => (
              <React.Fragment key={card.id}>
                {/* First slide - Left content */}
                <SwiperSlide>
                  <div className="flex flex-col justify-between min-h-[550px] rounded-2xl border border-gray-300 bg-black/5 overflow-hidden">
                    <div className="p-6 ">
                      <h3 className="text-[24px] text-center tracking-tight text-primary-700 mb-3">
                        {card.title}
                      </h3>
                      <p className="text-gray-700 text-[16px] text-center leading-relaxed">
                        {card.description}
                      </p>
                    </div>
                    <div className=" -mt-10">
                      <img
                        src={card.video}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </div>
                </SwiperSlide>

                {/* Second slide - Right content */}
                <SwiperSlide>
                  <div className="flex flex-col justify-between min-h-[550px] rounded-2xl border border-gray-300 bg-black/5 overflow-hidden">
                    <div className="p-6 ">
                      <h3 className="text-[24px] text-center tracking-tight text-primary-700 mb-3">
                        {card.LeftTitle}
                      </h3>
                      <p className="text-gray-700 text-[16px] text-center leading-relaxed">
                        {card.LeftDescription}
                      </p>
                    </div>
                    <div className=" aspect-video px-6 ">
                      <img
                        src={card.LeftVideo}
                        alt=""
                        className="h-full scale-150 left-16 relative w-full object-contain object-bottom rounded-lg"
                      />
                    </div>
                  </div>
                </SwiperSlide>
              </React.Fragment>
            ))}
          </Swiper>
        </div>

        {/* Flex layout on desktop */}
        <div className="hidden md:flex flex-col gap-16">
          {cards.map((card, index) => {
            const isEven = index % 2 !== 0;
            return (
              <AnimatedCard
                key={card.id}
                direction={isEven ? "right" : "left"}
                delay={0.1}
                once={false}
                resetOnExit={true}
                className="bg-transparent md:h-[100vh]"
              >
                <div
                  className={`flex flex-col ${
                    isEven ? "md:flex-row-reverse" : "md:flex-row"
                  } gap-8 relative min-h-[600px]`}
                >
                  {/* Left column */}
                  <div className="md:w-[40%] rounded-2xl border border-gray-300 bg-black/5 blur-bg relative flex flex-col overflow-hidden">
                    <div className="p-6 flex-1 flex flex-col justify-center">
                      <h3 className="md:text-[40px] text-[24px] text-center tracking-tight text-primary-700 mb-3">
                        {card.title}
                      </h3>
                      <p className="text-gray-700 text-[16px] text-center md:text-[20px] leading-relaxed">
                        {card.description}
                      </p>
                    </div>
                    <div className="mt-auto aspect-video">
                      <img
                        src={card.video}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Right column */}
                  <div className="md:w-[60%] rounded-2xl border border-gray-300 bg-black/5 shadow-lg relative flex flex-col">
                    <div className="p-6 flex-1 flex flex-col justify-center">
                      <h3 className="md:text-[40px] text-[24px] text-center tracking-tight text-primary-700 mb-3">
                        {card.LeftTitle}
                      </h3>
                      <p className="text-gray-700 text-[16px] text-center md:text-[20px] leading-relaxed">
                        {card.LeftDescription}
                      </p>
                    </div>
                    <div className="mt-auto aspect-video px-8">
                      <img
                        src={card.LeftVideo}
                        alt=""
                        className="h-full w-full object-contain object-bottom"
                      />
                    </div>
                  </div>
                </div>
              </AnimatedCard>
            );
          })}
        </div>
      </div>
    </section>
  );
}
